import React, { useState } from 'react'
import { Navigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import { getAuthorizedHospitalIds } from '../../context/AuthContext'
import './Login.css'

// Server-side login failures redirect to /login?error=<code> (see server.js). Map each
// code to a proper, user-facing message; anything unmapped falls back to the generic one.
const LOGIN_ERROR_MESSAGES: Record<string, string> = {
    project_unsupported: 'Your account is not associated with a dashboard available here. Please contact ILPQC if you believe this is an error.',
    unauthorized: 'Unauthorized Login!',
    server: 'Something went wrong while signing you in. Please try again.',
}

const Login: React.FC = () => {
    const auth = useAuth()
    const [searchParams] = useSearchParams()
    const [hospitalId, setHospitalId] = useState('')
    const [error, setError] = useState('')
    const [submitting, setSubmitting] = useState(false)
    const [devLoginError, setDevLoginError] = useState('')

    const loginErrorCode = searchParams.get('error')
    const loginErrorMessage = loginErrorCode
        ? LOGIN_ERROR_MESSAGES[loginErrorCode] || LOGIN_ERROR_MESSAGES.unauthorized
        : null

    // Dev-only: run the manual dev login and surface a message if the configured user is
    // rejected (no rights to the project) -- otherwise the click would appear to do nothing.
    const handleDevLogin = async () => {
        setDevLoginError('')
        const ok = await auth.devLogin()
        if (!ok) {
            setDevLoginError('Login failed — the REDCap key is invalid/expired, or the account is not authorized.')
        }
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        const trimmedId = hospitalId.trim()
        if (!/^\d{3}$/.test(trimmedId)) {
            setError('Invalid Hospital-ID')
            return
        }

        setSubmitting(true)
        setError('')

        try {
            const response = await fetch('/api/auth/validate-hospital', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ hospitalId: trimmedId }),
            })
            const data = await response.json()

            if (data.valid) {
                await auth.refresh()
            } else if (data.error === 'no_access') {
                setError('You do not have access to this hospital.')
            } else {
                setError('Invalid Hospital-ID')
            }
        } catch {
            setError('Unable to validate Hospital ID right now. Please try again.')
        } finally {
            setSubmitting(false)
        }
    }

    if (auth.loading) {
        // The project/dashboard isn't known yet at this point (the session hasn't resolved),
        // so there's no real title to show -- leave it blank rather than a placeholder that
        // doesn't match any actual dashboard.
        return <div className="login-page" />
    }

    // Practice admins skip the Hospital-ID entry entirely: their sites are known from the
    // username, so we send them straight to the dashboard with all sites in the URL.
    // Single-site (regular) users still enter their Hospital ID below.
    const isPracticeAdmin = auth.practiceAdminHospitals.length > 0
    if (auth.authenticated && auth.supported && (auth.hospitalId || isPracticeAdmin)) {
        const authorizedIds = getAuthorizedHospitalIds(auth)
        return <Navigate to={`${auth.defaultPath}?HospitalId=${authorizedIds.join(',')}`} replace />
    }

    // Error-only states show nothing but a red message -- no dashboard title, no "Login"
    // header: (a) an unauthorized user (no valid REDCap/ReportPortal session), or (b) a
    // valid user whose project has no dashboard here (unsupported / rejected project id).
    const unauthorized = !auth.authenticated && !auth.devLoginAvailable
    const projectUnsupported = auth.authenticated && !auth.supported
    if (unauthorized || projectUnsupported) {
        const message = unauthorized
            ? (loginErrorMessage || 'Unauthorized Login!')
            : "This project isn't available in this dashboard yet."
        return (
            <div className="login-page">
                <div className="login-box">
                    <div className="login-box-body">
                        <div className="login-message login-message-error">{message}</div>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="login-page">
            <div className="login-title">{auth.title || 'ILPQC Dashboard'}</div>

            <div className="login-box">
                <div className="login-box-header">Login</div>

                <div className="login-box-body">
                    {!auth.authenticated && auth.devLoginAvailable && (
                        <div className="login-logged-off">
                            <div className="login-message">You are not signed in.</div>
                            <button type="button" className="login-devlogin-btn" onClick={handleDevLogin}>
                                Log in
                            </button>
                            {devLoginError && (
                                <div className="login-message login-message-error">{devLoginError}</div>
                            )}
                        </div>
                    )}

                    {auth.authenticated && auth.supported && (
                        <form onSubmit={handleSubmit}>
                            {auth.username && (
                                <div className="login-welcome">Welcome , {auth.username}</div>
                            )}

                            <div className="login-hospital-row">
                                <label htmlFor="hospitalId">Hospital ID:</label>
                                <input
                                    id="hospitalId"
                                    type="text"
                                    value={hospitalId}
                                    onChange={(e) => setHospitalId(e.target.value)}
                                    autoFocus
                                    placeholder="Hospital ID"
                                    disabled={submitting}
                                />
                                <button type="submit" disabled={submitting}>
                                    {submitting ? '...' : 'Go'}
                                </button>
                            </div>

                            {error && <div className="login-alert">{error}</div>}
                        </form>
                    )}
                </div>
            </div>
        </div>
    )
}

export default Login
